<script>
const MINUTE_IN_MILLI_SECONDS = 60000;
const HOUR_IN_MILLI_SECONDS = MINUTE_IN_MILLI_SECONDS * 60;
const DAY_IN_MILLI_SECONDS = HOUR_IN_MILLI_SECONDS * 24;

import {
  shortTimestamp,
  dynamicTimeWithTimezone,
  dateFormatWithTimezone,
} from 'shared/helpers/timeHelper';
import { mapGetters } from 'vuex';

export default {
  name: 'TimeAgo',
  props: {
    isAutoRefreshEnabled: {
      type: Boolean,
      default: true,
    },
    lastActivityTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    createdAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    showDateWhenNotToday: {
      type: Boolean,
      default: false,
    },
    showYesterdayWhenNotToday: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      lastActivityAtTimeAgo: '',
      createdAtTimeAgo: '',
      timer: null,
      now: Date.now(),
    };
  },
  computed: {
    ...mapGetters({
      getCurrentAccountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
    }),
    accountTimezone() {
      const accountId = this.getCurrentAccountId;
      if (!accountId) return null;
      const account = this.getAccount(accountId);
      return account?.timezone || null;
    },
    uiLocale() {
      const rawLocale = this.$root?.$i18n?.locale || this.$i18n?.locale || 'en';
      const normalizedLocale =
        typeof rawLocale === 'string' ? rawLocale : rawLocale?.value || 'en';
      return normalizedLocale.replace('_', '-');
    },
    isLastActivityToday() {
      const lastActivitySeconds = Number(this.lastActivityTimestamp);
      if (!Number.isFinite(lastActivitySeconds)) return false;
      const lastActivityDate = new Date(lastActivitySeconds * 1000);
      const now = new Date();
      const lastActivityParts = this.getDateParts(lastActivityDate);
      const nowParts = this.getDateParts(now);
      return (
        lastActivityParts.year === nowParts.year &&
        lastActivityParts.month === nowParts.month &&
        lastActivityParts.day === nowParts.day
      );
    },
    isLastActivityYesterday() {
      const lastActivitySeconds = Number(this.lastActivityTimestamp);
      if (!Number.isFinite(lastActivitySeconds)) return false;
      const lastActivityDate = new Date(lastActivitySeconds * 1000);
      const yesterday = new Date(Date.now() - DAY_IN_MILLI_SECONDS);
      const lastActivityParts = this.getDateParts(lastActivityDate);
      const yesterdayParts = this.getDateParts(yesterday);
      return (
        lastActivityParts.year === yesterdayParts.year &&
        lastActivityParts.month === yesterdayParts.month &&
        lastActivityParts.day === yesterdayParts.day
      );
    },
    yesterdayLabel() {
      const translated = this.$t('REPORT.DATE_RANGE_OPTIONS.YESTERDAY');
      if (!translated || translated === 'REPORT.DATE_RANGE_OPTIONS.YESTERDAY') {
        return 'yesterday';
      }
      return String(translated).toLowerCase();
    },
    localizedLastActivityDate() {
      const lastActivitySeconds = Number(this.lastActivityTimestamp);
      if (!Number.isFinite(lastActivitySeconds)) return '';
      const lastActivityDate = new Date(lastActivitySeconds * 1000);
      const now = new Date();
      const shouldIncludeYear =
        this.getDateParts(lastActivityDate).year !==
        this.getDateParts(now).year;
      return this.formatLocalizedDate(lastActivityDate, shouldIncludeYear);
    },
    lastActivityTime() {
      if (this.showDateWhenNotToday && !this.isLastActivityToday) {
        if (this.showYesterdayWhenNotToday && this.isLastActivityYesterday) {
          return this.yesterdayLabel;
        }
        return this.localizedLastActivityDate;
      }
      const ts = Number(this.lastActivityTimestamp);
      if (Number.isFinite(ts)) {
        const diffSec = Math.floor((this.now - ts * 1000) / 1000);
        if (diffSec >= 0 && diffSec < 60) {
          return `${diffSec}s`;
        }
      }
      return shortTimestamp(this.lastActivityAtTimeAgo);
    },
    createdAtTime() {
      return shortTimestamp(this.createdAtTimeAgo);
    },
    createdAt() {
      const createdTimeDiff = Date.now() - this.createdAtTimestamp * 1000;
      const isBeforeAMonth = createdTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isBeforeAMonth
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.LATEST')} ${
            this.createdAtTimeAgo
          }`
        : `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.OLDEST')} ${dateFormatWithTimezone(
            this.createdAtTimestamp,
            'MMM d, yyyy',
            this.accountTimezone
          )}`;
    },
    lastActivity() {
      const lastActivityTimeDiff =
        Date.now() - this.lastActivityTimestamp * 1000;
      const isNotActive = lastActivityTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isNotActive
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.ACTIVE')} ${
            this.lastActivityAtTimeAgo
          }`
        : `${this.$t(
            'CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.NOT_ACTIVE'
          )} ${dateFormatWithTimezone(this.lastActivityTimestamp, 'MMM d, yyyy', this.accountTimezone)}`;
    },
    tooltipText() {
      return `${this.createdAt}
              ${this.lastActivity}`;
    },
  },
  watch: {
    lastActivityTimestamp() {
      this.updateTimeAgo();
    },
    createdAtTimestamp() {
      this.updateTimeAgo();
    },
    accountTimezone() {
      this.updateTimeAgo();
    },
  },
  mounted() {
    this.updateTimeAgo();
    if (this.isAutoRefreshEnabled) {
      this.createTimer();
    }
  },
  unmounted() {
    clearTimeout(this.timer);
  },
  methods: {
    getDateParts(date) {
      const formatter = new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        ...(this.accountTimezone ? { timeZone: this.accountTimezone } : {}),
      });

      return formatter.formatToParts(date).reduce((acc, part) => {
        if (part.type !== 'literal') {
          acc[part.type] = part.value;
        }
        return acc;
      }, {});
    },
    formatLocalizedDate(date, includeYear = false) {
      return new Intl.DateTimeFormat(this.uiLocale, {
        day: 'numeric',
        month: 'short',
        ...(includeYear ? { year: 'numeric' } : {}),
        ...(this.accountTimezone ? { timeZone: this.accountTimezone } : {}),
      }).format(date);
    },
    updateTimeAgo() {
      this.now = Date.now();
      const lastActivitySeconds = Number(this.lastActivityTimestamp);
      const createdAtSeconds = Number(this.createdAtTimestamp);

      const isLastActivityValid = Number.isFinite(lastActivitySeconds);
      const isCreatedAtValid = Number.isFinite(createdAtSeconds);

      this.lastActivityAtTimeAgo = isLastActivityValid
        ? dynamicTimeWithTimezone(lastActivitySeconds, this.accountTimezone)
        : '';

      this.createdAtTimeAgo = isCreatedAtValid
        ? dynamicTimeWithTimezone(createdAtSeconds, this.accountTimezone)
        : '';
    },
    createTimer() {
      this.timer = setTimeout(() => {
        this.updateTimeAgo();
        this.createTimer();
      }, this.refreshTime());
    },
    refreshTime() {
      const timeDiff = Date.now() - this.lastActivityTimestamp * 1000;
      if (timeDiff > DAY_IN_MILLI_SECONDS) {
        return DAY_IN_MILLI_SECONDS;
      }
      if (timeDiff > HOUR_IN_MILLI_SECONDS) {
        return HOUR_IN_MILLI_SECONDS;
      }
      // More frequent updates for very recent conversations
      if (timeDiff < MINUTE_IN_MILLI_SECONDS) {
        return 1000;
      }
      if (timeDiff < 5 * MINUTE_IN_MILLI_SECONDS) {
        return 10000; // 10 seconds for messages less than 5 minutes old
      }
      if (timeDiff < 30 * MINUTE_IN_MILLI_SECONDS) {
        return 30000; // 30 seconds for messages less than 30 minutes old
      }

      return MINUTE_IN_MILLI_SECONDS;
    },
  },
};
</script>

<template>
  <div
    v-tooltip.top="{
      content: tooltipText,
      delay: { show: 1000, hide: 0 },
      hideOnClick: true,
    }"
    class="ml-auto leading-4 text-xxs text-n-slate-10 hover:text-n-slate-11"
  >
    <span>{{ lastActivityTime }}</span>
  </div>
</template>
